import { useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { bridgeStatusQueryKey, fetchBridgeStatus } from "@common/lib/api/bridge";
import type { BridgeIntegrationId } from "@common/lib/api/bridge";
import { AccountContext } from "@/state/providers/AccountProvider";
import {
    bridgeIntegrationForPims,
    hasBridgeWriteback,
    isResumableStep,
} from "@/features/bridge-setup/bridge-setup-config";
import { bothChannelsConfirmed } from "@/state/hooks/useBridgeSetup";
import { classifyNetworkError } from "@common/lib/sentryUtils";

/** Where the clinic is, so each surface points at the one thing left instead of pitching from scratch. */
export type BridgeUpgradeStage = "start" | "finishInstall" | "finishSetup";

export interface BridgeUpgradeOffer {
    eligible: boolean;
    integration: BridgeIntegrationId | null;
    /** Null exactly when `eligible` is false. */
    stage: BridgeUpgradeStage | null;
}

/**
 * A practice we can move onto the bridge without taking something away from them, or one we cannot
 * identify at all.
 *
 * "Has a bridge module" and "can safely be moved" are different questions: a module can read fine
 * and have no writeback, which makes taking this offer a downgrade. ImproMed is that case today, so
 * this reads `hasBridgeWriteback` rather than restating it.
 *
 * Exported because `BridgeSetupFlow`'s `mayUseBridgeWizard` is a second door onto the same wizard
 * and must not drift from this one. Truthy check so a legacy empty string counts as "couldn't tell"
 * too — that branch is permissive on purpose, and safe only because the wizard's picker offers
 * `INSTALLABLE_INTEGRATION_IDS`, so an unidentified practice cannot pick a PIMS with no writeback.
 */
export const pimsCanMoveToBridge = (currentPims: string | null | undefined): boolean => {
    const integration = bridgeIntegrationForPims(currentPims);
    if (integration !== null) return hasBridgeWriteback(integration);
    return !currentPims;
};

/** Why a cohort account is NOT being shown the offer. Reported as a Sentry breadcrumb. */
type BridgeOfferHiddenReason =
    | "pims_excluded"
    | "status_loading"
    | "status_failed"
    | "setup_finished"
    | "installation_inactive";

/**
 * Whether this account should be pitched the HappyBridge upgrade, and which PIMS to name.
 *
 * One hook rather than a condition per surface: the offer appears app-wide, on Settings and on the
 * Usage dashboard, and a strip pitching what a card has already stopped pitching looks like nothing
 * is wrong. A new surface renders `BridgeUpgradeCta` and inherits this gate.
 *
 * Eligible when the account is in the cohort, setup isn't finished, the installation is not retired
 * (`is_active: false` is never offered), and either their PIMS can be
 * moved (`pimsCanMoveToBridge` — writeback exists, so Cornerstone and AVImark, not ImproMed) or we
 * cannot tell what they run at all.
 *
 * `bridge_upgrade_offer`, not `happy_bridge_beta`: "should be pitched" is not "is in the beta".
 * They were one column, and since nearly every beta account had already finished setup, the offer
 * rendered for zero real clinics — the mechanical reason hand-sent upgrade links never got booked.
 *
 * The "couldn't tell" case is load-bearing, not a nicety: requiring a recognised `current_pims`
 * locked out exactly the accounts the wizard's PIMS picker exists to serve. An unrecognised but
 * PRESENT value stays excluded, which is right for a cloud PIMS and an accepted false negative for
 * a typo of an on-prem one (`pims-correction.ts` exists for that; the breadcrumb below makes each
 * instance visible).
 *
 * Deliberately does NOT require a live third-party integration — that excluded the clinics with
 * least to lose by switching, including ones whose connection had already broken.
 *
 * NOT gated on account ownership, though the wizard performs an irreversible flip: `POST
 * /bridge/setup-integration` has never enforced a role, so gating here hides a door while leaving
 * the endpoint open, and the real fix is server-side. Tracked separately.
 *
 * Shares one cache entry with every other reader of `bridgeStatusQueryKey` (`BridgeStatus`,
 * `Integrations`), so a page mounting all three makes one request. Key AND `queryFn` are imported,
 * not repeated: React Query uses whichever observer mounted last, so two `queryFn`s behind one key
 * would make behaviour depend on mount order — and `fetchBridgeStatus` is the one that throws on a
 * failed check rather than resolving to something that looks like `installed: false`.
 *
 * Reads `AccountContext` directly rather than through `useAccountContext` so it cannot throw when
 * mounted outside the provider.
 */
export function useBridgeUpgradeOffer(): BridgeUpgradeOffer {
    const accountContext = useContext(AccountContext);
    const accountData = accountContext?.accountData;

    const inCohort = accountData?.bridge_upgrade_offer === true;
    const integration = bridgeIntegrationForPims(accountData?.current_pims);
    const gated = inCohort && pimsCanMoveToBridge(accountData?.current_pims);

    const { data, error, isError } = useQuery({
        queryKey: bridgeStatusQueryKey(accountData?.id),
        queryFn: fetchBridgeStatus,
        staleTime: 60_000,
        // No point asking whether the bridge is installed for an account that couldn't see
        // this offer anyway.
        enabled: gated,
    });

    /**
     * Finished means installed AND done — either alone leaves a clinic mid-migration with this
     * offer as their way back.
     *
     * The second clause mirrors the wizard's own exit condition. An install predating the step
     * marker, or migrated by hand, is fully live with `setup_step` null, and `BridgeSetupFlow`
     * already bounces that clinic to Integrations (`alreadySetUp && !hasResumableStep`; null is not
     * resumable) — so the offer stayed up with a `Start Upgrade` that bounced straight back.
     *
     * Gated on HEALTH, not on "step unknown": a null step on an installed but unhealthy bridge is
     * the genuinely resumable case, and hiding the offer there strands them. Same predicates the
     * wizard uses, imported so the two cannot disagree about who is finished.
     */
    const health = data?.latest_health;
    const fullyLive =
        data?.installed === true &&
        data.is_active === true &&
        health?.status === "healthy" &&
        bothChannelsConfirmed(health);
    const setupFinished =
        data?.installed === true && (data.setup_step === "done" || (fullyLive && !isResumableStep(data.setup_step)));

    // A retired installation (`is_active: false`, e.g. after decommission) cannot progress (re-registering
    // does not reactivate it). Re-enabling `bridge_upgrade_offer` in Spork does not show the offer for a
    // retired installation; reactivate the installation first. Checked BEFORE
    // `setup_finished` so the breadcrumb names the more specific reason.
    const installationInactive = data?.installed === true && data.is_active === false;

    // Every way this offer fails to appear looks identical from outside: a blank page. That is how
    // the bug this ticket fixes went unnoticed for months, and there is no product-analytics SDK to
    // catch it (by design; see CLAUDE.md). So a cohort account we are NOT pitching says why.
    // A breadcrumb, not `captureMessage` — this is not an error, and CLAUDE.md forbids that.
    const hiddenReason: BridgeOfferHiddenReason | null = !inCohort
        ? null
        : !gated
          ? "pims_excluded"
          : !data
            ? isError
                ? "status_failed"
                : "status_loading"
            : installationInactive
              ? "installation_inactive"
              : setupFinished
                ? "setup_finished"
                : null;

    // Derived FROM `hiddenReason`, not re-derived beside it: these were two independent
    // expressions that happened to agree, and a drift would make the breadcrumb lie.
    // `inCohort &&` because `hiddenReason` is null for accounts outside the cohort too. Note the
    // `!data` arm keeps the offer hidden while status is loading or failed — pitching a practice
    // that already upgraded is worse than showing the offer a beat late.
    const eligible = inCohort && hiddenReason === null;

    // Where the clinic is, so the offer points at the one thing left instead of pitching an upgrade
    // it has mostly done (EN-1333). Installed at all means the wizard's later steps remain. With a
    // time zone set it resumes such a clinic at install, even with only a `computer`/`timezone`
    // marker saved; with no time zone (or no active installation) it starts at the first screen.
    // Not installed but with a switch-over on record means the download happened and the installer
    // never ran on the server. A null cutover stays "start" even with a saved step: nothing shows
    // a download happened. With a saved `install` step the wizard still resumes them at install;
    // with an earlier step it starts at the first screen.
    const stage: BridgeUpgradeStage | null = !eligible
        ? null
        : data?.installed === true
          ? "finishSetup"
          : data?.cutover_state === "pending" || data?.cutover_state === "done"
            ? "finishInstall"
            : "start";

    useEffect(() => {
        if (!hiddenReason) return;
        Sentry.addBreadcrumb({
            category: "bridge-upgrade",
            level: "info",
            message: `Bridge upgrade offer hidden for a cohort account: ${hiddenReason}`,
            data: { reason: hiddenReason, current_pims: accountData?.current_pims ?? null },
        });
    }, [hiddenReason, accountData?.current_pims]);

    // A BACKSTOP — `apiFetch` already reports network failures with better context. What only this
    // covers is the failure it cannot see: a 204/205 is a SUCCESS to `apiFetch`, and the throw comes
    // afterwards from `expectJsonBody`, outside its try/catch. That case silently removes the only
    // in-app route to the wizard for the whole cohort. `classifyNetworkError` rather than a
    // hardcoded "connectivity", because an empty body is a backend contract bug, not a network one.
    useEffect(() => {
        if (!error) return;
        Sentry.captureException(error, { tags: { errorCategory: classifyNetworkError(error) } });
    }, [error]);

    return { eligible, integration, stage };
}
